import { fetcher, type ApiEnvelope } from "@/lib/api";
import { trackChokh } from "@/lib/chokh";
import type {
  CodeExplainResult,
  CodeFixResult,
  ExecutionResult,
  ExplainCodeInput,
  FixErrorFeedbackInput,
  FixErrorInput,
  IdePlanResponse,
  LanguagesListResponse,
  MySnippetsResponse,
  PlaygroundHealth,
  PublicSharedSnippet,
  RunCodeInput,
  RuntimeCatalogue,
  SaveSnippetInput,
  SavedSnippet,
  SharedSnippetAccessGrant,
  ShareSnippetInput,
  SnippetVersion,
  SnippetVersionsResponse,
  SnippetVersionSummary,
  UpdateSnippetInput,
} from "./types";
import { guardExecutionResultForRendering } from "./lib/run-status";

function unwrap<T>(res: ApiEnvelope<T>): T {
  if (!res.success) {
    throw new Error((res as { error?: { message?: string } }).error?.message ?? "Request failed");
  }
  return res.data;
}

// ── Public ─────────────────────────────────────────────────────────────────

export async function runCode(input: RunCodeInput): Promise<ExecutionResult> {
  const res = await fetcher<ExecutionResult>("/v1/playground/run", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const result = guardExecutionResultForRendering(unwrap(res));
  trackChokh("playground_run", { languageId: input.languageId });
  return result;
}

/**
 * Fix My Error (`PG-AI02`, ADR-0051).
 *
 * The key travels twice, header and body, as Explain does: the backend refuses
 * a mismatch, which is what stops a proxy or a retry from quietly addressing a
 * different operation than the one the body describes.
 */
export async function fixError(input: FixErrorInput): Promise<CodeFixResult> {
  const res = await fetcher<CodeFixResult>("/v1/playground/fix-error", {
    method: "POST",
    headers: { "Idempotency-Key": input.idempotencyKey },
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

/** Did it help, and did the student take the fix (`PG-AI02`). Counts only. */
export async function sendFixErrorFeedback(input: FixErrorFeedbackInput): Promise<void> {
  await fetcher("/v1/playground/fix-error/feedback", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function fetchLanguages(): Promise<LanguagesListResponse["languages"]> {
  const res = await fetcher<LanguagesListResponse>("/v1/playground/languages");
  const data = unwrap(res);
  return data.languages;
}

/**
 * The version catalogue plus whether anything has confirmed the deployed
 * toolchains lately (`PG-IDE01`).
 *
 * Separate from `/languages` because the discovery annotation is the part that
 * moves; the selectable set behind it is bundled with the app.
 */
export async function fetchRuntimeCatalogue(): Promise<RuntimeCatalogue> {
  const res = await fetcher<RuntimeCatalogue>("/v1/playground/runtimes");
  return unwrap(res);
}

/**
 * Public shared snippet by nanoid slug (non–ObjectId).
 * Same path as `fetchSnippetById` — backend disambiguates id vs slug.
 */
export async function fetchSharedSnippet(shareSlug: string): Promise<PublicSharedSnippet> {
  const res = await fetcher<{ snippet: PublicSharedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(shareSlug)}`,
  );
  return unwrap(res).snippet;
}

/**
 * The embed payload (`PG-EMBED01`, ADR-0050).
 *
 * Anonymous on purpose: the iframe runs inside a third party's page, so no
 * token, no cookie and no silent refresh leave it. A snippet that is private,
 * password-protected, unshared, missing or simply not enabled for embedding
 * answers one identical 404, which is why the caller renders one identical
 * card rather than branching on a reason.
 */
export async function fetchEmbeddedSnippet(shareSlug: string): Promise<PublicSharedSnippet> {
  const res = await fetcher<{ snippet: PublicSharedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(shareSlug)}/embed`,
    undefined,
    undefined,
    { anonymous: true },
  );
  return unwrap(res).snippet;
}

/** The embed's Run button: the ordinary run route, as nobody (`PG-EMBED01`). */
export async function runCodeAnonymously(input: RunCodeInput): Promise<ExecutionResult> {
  const res = await fetcher<ExecutionResult>(
    "/v1/playground/run",
    { method: "POST", body: JSON.stringify(input) },
    undefined,
    { anonymous: true },
  );
  const result = guardExecutionResultForRendering(unwrap(res));
  trackChokh("playground_run", { languageId: input.languageId, embed: true });
  return result;
}

export async function verifySharedSnippetAccess(
  shareSlug: string,
  password: string,
): Promise<SharedSnippetAccessGrant> {
  const res = await fetcher<SharedSnippetAccessGrant>(
    `/v1/playground/snippets/${encodeURIComponent(shareSlug)}/access`,
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  );
  return unwrap(res);
}

/** Authenticated: load own snippet by MongoDB id. */
export async function fetchSnippetById(id: string): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}`,
  );
  return unwrap(res).snippet;
}

export async function fetchPlaygroundHealth(): Promise<PlaygroundHealth> {
  const res = await fetcher<PlaygroundHealth>("/v1/playground/health");
  return unwrap(res);
}

// ── Authenticated (snippets) ───────────────────────────────────────────────

export type ListMySnippetsParams = {
  page?: number;
  limit?: number;
  languageId?: string;
  /** Filter by folder ObjectId, or the literal `"null"` for uncategorized. */
  folderId?: string;
  /** Title search, applied on the server before pagination (`PG-UX06`). Empty means none. */
  q?: string;
};

export async function fetchIdePlan(): Promise<IdePlanResponse> {
  const res = await fetcher<IdePlanResponse>("/v1/playground/me/plan");
  return unwrap(res);
}

export async function explainCode(input: ExplainCodeInput): Promise<CodeExplainResult> {
  const res = await fetcher<CodeExplainResult>("/v1/playground/explain", {
    method: "POST",
    headers: { "Idempotency-Key": input.idempotencyKey },
    body: JSON.stringify(input),
  });
  return unwrap(res);
}

export async function fetchMySnippets(params?: ListMySnippetsParams): Promise<MySnippetsResponse> {
  const q = new URLSearchParams();
  if (params?.page != null) q.set("page", String(params.page));
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.languageId) q.set("languageId", params.languageId);
  if (params?.folderId !== undefined) q.set("folderId", params.folderId);
  if (params?.q) q.set("q", params.q);
  const qs = q.toString();
  const res = await fetcher<MySnippetsResponse>(`/v1/playground/snippets${qs ? `?${qs}` : ""}`);
  return unwrap(res);
}

export async function saveSnippet(input: SaveSnippetInput): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>("/v1/playground/snippets", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return unwrap(res).snippet;
}

// ── Version history (PG-IDE05, owner only) ─────────────────────────────────

/** The owner's history for one snippet, newest first, without the source. */
export async function fetchSnippetVersions(id: string): Promise<SnippetVersionSummary[]> {
  const res = await fetcher<SnippetVersionsResponse>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/versions`,
  );
  return unwrap(res).versions;
}

/** One version, with the source, for the diff and the restore. */
export async function fetchSnippetVersion(id: string, versionId: string): Promise<SnippetVersion> {
  const res = await fetcher<{ version: SnippetVersion }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
  );
  return unwrap(res).version;
}

export async function deleteSnippetVersion(id: string, versionId: string): Promise<void> {
  await fetcher<{ deleted: true }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    { method: "DELETE" },
  );
}

export async function updateSnippet(id: string, input: UpdateSnippetInput): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return unwrap(res).snippet;
}

export async function deleteSnippet(id: string): Promise<{ deleted: true }> {
  const res = await fetcher<{ deleted: true }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    },
  );
  return unwrap(res);
}

export async function forkSnippet(id: string): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/fork`,
    { method: "POST" },
  );
  return unwrap(res).snippet;
}

export async function shareSnippet(
  id: string,
  input: ShareSnippetInput = {},
): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/share`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return unwrap(res).snippet;
}

export async function unshareSnippet(id: string): Promise<SavedSnippet> {
  const res = await fetcher<{ snippet: SavedSnippet }>(
    `/v1/playground/snippets/${encodeURIComponent(id)}/share`,
    { method: "DELETE" },
  );
  return unwrap(res).snippet;
}
